import moment, { MomentInput } from 'moment';

// return milisec from today to a given date
// E.G. now is  January 13, 2022 00:00 and the given date is
export const timeFromNow = (timeInMilliseconds: number): number => {
  return moment().diff(timeInMilliseconds);
};
// return days from today to a given date
export const daysFromNow = (timeInMilliseconds?: number): number => {
  return moment().diff(timeInMilliseconds, 'days');
};

// return years from today to a given date (E.G. 2024 => 2)
export const yearsFromNow = (timeInMilliseconds?: number): number => {
  return moment().diff(timeInMilliseconds, 'years');
};
// is timeInMiliseconds input is in the past
export const isTimeInPast = (timeInMiliseconds?: string): boolean => {
  return moment(timeInMiliseconds).diff(moment()) < 0;
};
// get year of a given date (or today's if undefined)
export const getYear = (timeInMilliseconds?: string | number | undefined): number => {
  return moment(timeInMilliseconds).year();
};

// get month index (number E.G. Jan=0, Apr=3 ) of a given date (or todays if undefined)
export const getMonth = (timeInMilliseconds: string | undefined): number => {
  return moment(timeInMilliseconds).month();
};

// adds X days to a given date. If timeInMiliseconds is undefined it will be x days from 'now'
export const addDays = ({
  timeInMiliseconds,
  daysToAdd,
}: {
  timeInMiliseconds: number | undefined | moment.Moment;
  daysToAdd: number;
}): moment.Moment => {
  return moment(timeInMiliseconds).add(daysToAdd, 'days');
};

// adds X years to a given date. If timeInMiliseconds is undefined it will be x years from 'now'
export const addYears = ({
  timeInMiliseconds,
  yearsToAdd,
}: {
  timeInMiliseconds: number | undefined | moment.Moment;
  yearsToAdd: number;
}): moment.Moment => {
  return moment(timeInMiliseconds).add(yearsToAdd, 'years');
};

export const isAfter = (value1: number | string | moment.Moment, value2: number | string | moment.Moment): boolean => {
  return moment(value1).isAfter(moment(value2));
};

export const startOfDay = (date: Date) => moment(date).startOf('day').toDate();

// Parse Unix timestamp (in seconds) to Date object
export const parseUnixTimestamp = (timestamp: number): Date => {
  return moment.unix(timestamp).toDate();
};

export function convertISOToUnix(date: MomentInput) {
  return moment(date).unix();
}

// Get relative time string with "ago" suffix (e.g., "2 hours ago")
export const fromNow = (time: number | string | Date, timeOnly = false): string => {
  return moment(time).fromNow(timeOnly);
};

// Get relative time from Unix timestamp
export const fromNowUnix = (timestamp: number, timeOnly = false): string => {
  return moment.unix(timestamp).fromNow(timeOnly);
};

// Get difference in days between two dates (returns positive number for past dates)
export const diffInDays = (date1: number | string | Date, date2: number | string | Date): number => {
  return moment(date1).diff(moment(date2), 'days');
};

// Get difference in days from now (positive for past dates)
export const diffInDaysFromNow = (date: number | string | Date): number => {
  return moment().diff(moment(date), 'days');
};

// Get difference in days from now for Unix timestamp (positive for past dates)
export const findDaysAgoDiff = ({ date }: { date: number }): number => {
  return diffInDaysFromNow(parseUnixTimestamp(date));
};

// Get start of month for a date
export const startOfMonth = (date: Date): Date => {
  return moment(date).startOf('month').toDate();
};

// Get end of month for a date
export const endOfMonth = (date: Date): Date => {
  return moment(date).endOf('month').toDate();
};

// Add months to a date
export const addMonths = (date: Date | string | number, count: number): Date => {
  return moment(date).add(count, 'months').toDate();
};

// Subtract seconds from a date/timestamp
export const subtractSeconds = (date: Date | string | number, count: number): Date => {
  return moment(date).subtract(count, 'seconds').toDate();
};

// Add time to a date with specified unit (generic add function)
export const addTime = (
  date: Date | string | number,
  count: number,
  unit: 'days' | 'weeks' | 'months' | 'years' | 'hours' | 'minutes' | 'seconds',
): Date => {
  return moment(date).add(count, unit).toDate();
};

// Validate time string in HH:MM:SS format
export const isValidHoursMinutesSecondsFormat = (time: string): boolean => {
  return moment(time, 'HH:mm:ss', true).isValid();
};

// Validate ISO-8601 date string (YYYY-MM-DD, optionally with a time component).
export const isValidISODate = (date: string): boolean => {
  return moment(date, moment.ISO_8601, true).isValid();
};

// Format minutes-from-midnight as a wall-clock time string.
// e.g. minutesToTimeString(7 * 60 + 30, 'h:mm A') → "7:30 AM"
export const minutesToTimeString = (minutes: number, format: string): string => {
  return moment().startOf('day').add(minutes, 'minutes').format(format);
};
